import { Injectable } from "@angular/core";
import { ReplaySubject } from "rxjs";
import { Store } from "@ngxs/store";
import { SetFilter } from "../state/apai.actions";
import { CompaniesService } from "../../../services/companies.service";
import { CompanyProducts } from "src/models/phoenix";
import { AuthNewService } from "src/auth/services/auth-new.service";

export interface CompaniesEnabled {
  userId: number;
  userRole: string;
  companies: {
    id: string;
    name: string;
    countryCode?: string | null;
    accountName: string;
    enabled: boolean;
  }[];
}

export interface AuthSessionPayload {
  userId: string;
  role: string;
  companiesIds: string[];
}

@Injectable({
  providedIn: "root",
})
export class ApaiEnabledService {
  enabledCompaniesSubject = new ReplaySubject<CompaniesEnabled>();
  enabledCompanies$ = this.enabledCompaniesSubject.asObservable();
  enabledCompanies: CompaniesEnabled;

  constructor(
    private ngxsStore: Store,
    private companiesService: CompaniesService,
    private auth: AuthNewService,
  ) {}

  fetchConfig() {
    const payload = this.getAuthPayload();

    if (!payload) {
      console.warn("No authenticated user data found");
      return;
    }

    const companiesIds = payload.companiesIds || [];
    const isAdmin = this.hasRole(payload.role, "Admin");

    if (payload.userId) {
      const entry = localStorage.getItem(
        "apai_enabled_companies" + payload.userId,
      );

      if (entry) {
        try {
          const parsed: CompaniesEnabled = JSON.parse(entry);
          this.enabledCompanies = parsed;

          this.enabledCompaniesSubject.next(parsed);
        } catch (_) {
          console.error("Error parsing stored APAI enabled companies data");
        }
      }
    }

    const companyIds = isAdmin ? [] : companiesIds; // Admins have all companies available

    const sub = this.companiesService.companiesWithEnabledProducts(
      CompanyProducts.apai,
      companyIds,
    );
    sub.subscribe({
      next: (companies) => {
        const enabledCompaniesData: CompaniesEnabled = {
          userId: Number(payload.userId),
          userRole: payload.role,
          companies,
        };

        this.enabledCompanies = enabledCompaniesData;
        this.enabledCompaniesSubject.next(enabledCompaniesData);

        localStorage.setItem(
          "apai_enabled_companies" + payload.userId,
          JSON.stringify(enabledCompaniesData),
        );

        const firstEnabledCompany = companies.find((c) => c.enabled);
        if (firstEnabledCompany) {
          this.ngxsStore.dispatch(
            new SetFilter({
              companyId: firstEnabledCompany.id,
              page: 1,
            }),
          );
        }
      },
      error: (error) => {
        console.error("Error fetching company enabled products:", error);
      },
    });
    return sub;
  }

  private getAuthPayload(): AuthSessionPayload | null {
    const payload = this.auth.userValue?.payload;
    if (!payload) return null;

    return {
      userId: payload.userId,
      role: payload.role?.toString(),
      companiesIds: payload.companiesIds || [],
    };
  }

  private hasRole(value: string | undefined, role: string) {
    return value?.toLowerCase() === role.toLowerCase();
  }
}
